// src/components/layout/Footer.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../../utils/constants";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // State for dynamically fetched services
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);

  // Hardcoded locations (these are business constants, not from API)
  const locations = [
    {
      city: "Santo Domingo",
      address: "C. Rafael J Castillo 88, Santo Domingo 10510",
      details: "Entre Americo Lugo y Arturio Loroño, Ensanche La Fe",
    },
    {
      city: "Nagua",
      address: "Calle Duarte, Plaza Alemán Victoria",
      details: "Ciudad de Nagua",
    },
    {
      city: "Santiago Rodríguez",
      address: "Calle Duarte, Villa Los Almacigos",
      details: "Próximo a la Plaza de la Bandera",
    },
    {
      city: "Villa Vásquez, Monte Cristi",
      address: "Calle Avenida Libertad 95, Plaza Belowi",
      details: "Villa Vásquez",
    },
  ];

  /**
   * Fetch services from backend API on component mount
   *
   * This replaces the hardcoded services list with dynamic data from
   * the Django backend, ensuring the footer always shows the current
   * services you have configured in the admin panel. When you add,
   * remove, or modify a service through the admin, the footer updates
   * automatically without requiring a frontend code change.
   */
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/services/`);

        if (response.ok) {
          const data = await response.json();

          // Handle both paginated and non-paginated responses
          // If the response has a 'results' property, it's paginated
          const servicesArray = data.results
            ? data.results
            : Array.isArray(data)
              ? data
              : [];

          setServices(servicesArray);
        }
      } catch (err) {
        console.error("Error fetching services for footer:", err);
        // On error, services will remain empty — footer degrades gracefully
      } finally {
        setServicesLoading(false);
      }
    };

    fetchServices();
  }, []);

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer Content */}
      <div className="container-custom py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Information Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img
                src="/logo-footer.png"
                srcSet="/logo-footer@2x.png 2x"
                alt="ManejaRD"
                className="h-10 w-auto"
              />
            </div>

            <p className="text-sm text-gray-400 leading-relaxed">
              Tu camino hacia conducir con confianza. Cursos intensivos de alta
              calidad con instructores certificados y vehículos modernos.
            </p>

            {/* Social Media Links */}
            <div className="flex space-x-4 pt-2">
              <a
                href="https://facebook.com/manejadr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-primary transition-colors duration-300"
                aria-label="Facebook"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>

              <a
                href="https://instagram.com/manejadr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-primary transition-colors duration-300"
                aria-label="Instagram"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>

              <a
                href="https://wa.me/18299064722"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-[#25D366] transition-colors duration-300"
                aria-label="WhatsApp"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links Section */}
          <div>
            <h3 className="text-white font-heading font-bold text-lg mb-4">
              Enlaces Rápidos
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/"
                  className="text-sm hover:text-primary transition-colors duration-300"
                >
                  Inicio
                </Link>
              </li>
              <li>
                <Link
                  to="/servicios"
                  className="text-sm hover:text-primary transition-colors duration-300"
                >
                  Nuestros Servicios
                </Link>
              </li>
              <li>
                <Link
                  to="/citas"
                  className="text-sm hover:text-primary transition-colors duration-300"
                >
                  Agendar Cita
                </Link>
              </li>
              <li>
                <Link
                  to="/contacto"
                  className="text-sm hover:text-primary transition-colors duration-300"
                >
                  Contacto
                </Link>
              </li>
            </ul>
          </div>

          {/* Services Section — Dynamic from API */}
          <div>
            <h3 className="text-white font-heading font-bold text-lg mb-4">
              Servicios
            </h3>

            {servicesLoading ? (
              // Skeleton loading state while fetching services from backend
              <ul className="space-y-2 text-sm">
                {[1, 2, 3, 4].map((i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <span className="text-primary mt-1">•</span>
                    <span className="bg-gray-700 rounded h-4 w-32 animate-pulse" />
                  </li>
                ))}
              </ul>
            ) : services.length > 0 ? (
              // Dynamic services list from backend API
              <ul className="space-y-2 text-sm">
                {services.map((service) => (
                  <li key={service.id} className="flex items-start space-x-2">
                    <span className="text-primary mt-1">•</span>
                    <Link
                      to={`/servicios#${service.slug}`}
                      className="hover:text-primary transition-colors duration-300"
                    >
                      {service.name}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              // Fallback if API is unreachable — shows the most essential services
              <ul className="space-y-2 text-sm">
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Curso Básico</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Curso Intermedio</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Curso Avanzado</span>
                </li>
              </ul>
            )}
          </div>

          {/* Contact Information Section */}
          <div>
            <h3 className="text-white font-heading font-bold text-lg mb-4">
              Contacto
            </h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-primary flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <a
                  href="tel:+18299064722"
                  className="hover:text-primary transition-colors duration-300"
                >
                  (829) 906-4722
                </a>
              </li>

              <li className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-primary flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <a
                  href="mailto:info@manejard.com"
                  className="hover:text-primary transition-colors duration-300"
                >
                  info@manejard.com
                </a>
              </li>

              <li className="flex items-start space-x-3">
                <svg
                  className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <div>Lun - Vie: 8:30 AM - 5:30 PM</div>
                  <div>Sáb: 8:30 AM - 3:30 PM</div>
                  <div>Dom: Cerrado</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Locations Section */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <h3 className="text-white font-heading font-bold text-lg mb-6 text-center">
            Nuestras Ubicaciones
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {locations.map((location, index) => (
              <div key={index} className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <svg
                    className="w-5 h-5 text-primary mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  {location.city}
                </h4>
                <p className="text-sm text-gray-400 mb-1">{location.address}</p>
                <p className="text-xs text-gray-500">{location.details}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              © {currentYear} ManejaRD. Todos los derechos reservados.
            </p>

            <div className="flex space-x-6 text-sm">
              <Link
                to="/privacidad"
                className="text-gray-400 hover:text-primary transition-colors duration-300"
              >
                Política de Privacidad
              </Link>
              <Link
                to="/terminos"
                className="text-gray-400 hover:text-primary transition-colors duration-300"
              >
                Términos de Servicio
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
